import {
	ApplicationRef,
	ComponentRef,
	EnvironmentInjector,
	Injectable,
	Type,
	createComponent,
} from '@angular/core';

/**
 * Motor de overlays próprio (sem Ionic). Renderiza modal/alert/loading/toast/popover
 * anexando um host no <body>, com backdrop + wrapper animados e uma pilha (getTop/dismiss
 * do topo). Substitui ModalController/AlertController/LoadingController/ToastController/
 * PopoverController do @ionic/angular mantendo a mesma API (create → present/dismiss/
 * onDidDismiss). Ver docs/desionizacao-ionic.md (Fase 1).
 */

export type OverlayRole = 'backdrop' | 'cancel' | 'gesture' | string;

export interface OverlayDismissEvent<T = any> {
	data?: T;
	role?: OverlayRole;
}

export interface OverlayHandle<T = any> {
	present(): Promise<void>;
	dismiss(data?: any, role?: OverlayRole): Promise<boolean>;
	onDidDismiss<D = T>(): Promise<OverlayDismissEvent<D>>;
	onWillDismiss<D = T>(): Promise<OverlayDismissEvent<D>>;
	readonly hostElement: HTMLElement;
	/** Compat: no Ionic o getTop() devolve o elemento do modal; delega ao host. */
	querySelector<E extends Element = Element>(selectors: string): E | null;
}

/** Os tipos de overlay. No Ionic cada um é um elemento próprio (`ion-modal`, `ion-loading`,
 *  …) com a sua própria pilha; aqui a pilha é uma só e o tipo é o que a separa. */
export type OverlayKind = 'modal' | 'alert' | 'loading' | 'toast' | 'popover';

export interface OverlayAttachConfig {
	kind: OverlayKind;
	cssClass?: string | string[];
	backdropDismiss?: boolean;
	showBackdrop?: boolean;
	animated?: boolean;
	duration?: number;
	position?: 'top' | 'bottom' | 'middle';
	/** Conteúdo por componente Angular (modal/popover). */
	component?: Type<any>;
	componentProps?: Record<string, any>;
	/** Conteúdo por DOM pré-montado (alert/loading/toast). */
	element?: HTMLElement;
	/** Âncora do popover (posição do clique). */
	positionTarget?: { x: number; y: number };
	/**
	 * Injector de ambiente de quem abriu o overlay (ver `MpModalController`). Sem ele o
	 * componente nasce no injector raiz e não enxerga os providers do módulo lazy da página.
	 */
	environmentInjector?: EnvironmentInjector;
	/**
	 * O "voltar" do navegador (botão/gesto do Android, borda do Safari) age NESTE overlay em vez
	 * de navegar a página de baixo. Ver `pushHistoryGuard`.
	 *
	 * Sem isto, com o overlay aberto, `history.back()` navegava a rota de BAIXO e o overlay — que
	 * vive no <body> — continuava por cima, idêntico: o cliente achava que o voltar não fez nada,
	 * e quando concluía caía numa página que já não era a que abriu o overlay. Reproduzido em
	 * produção na tela de endereço (24/09/2026): `/delivery` virava `/shopping-cart` por baixo.
	 * ~60 aparelhos/dia, concluindo 83% × 97%.
	 *
	 * Quando o "voltar" chega, o overlay chama `onHistoryBack()` do componente, se existir (a tela
	 * decide: voltar uma etapa, fechar…); senão fecha com role `'back'`.
	 */
	interceptHistoryBack?: boolean;
}

const ENTER_MS = 280;
const LEAVE_MS = 200;
/** Teto de espera pelo `popstate` do próprio `history.back()` ao fechar (ver `consumeHistoryGuard`). */
const HISTORY_CONSUME_MS = 400;
/** Marca no `history.state` da entrada empilhada pelo overlay. */
const GUARD_KEY = 'mpOverlayGuard';

interface OverlayInstance {
	handle: OverlayHandle;
	kind: OverlayKind;
	hostEl: HTMLElement;
	componentRef?: ComponentRef<any>;
	previousActive?: HTMLElement | null;
	keyHandler?: (e: KeyboardEvent) => void;
	autoDismissTimer?: any;
	interceptHistoryBack?: boolean;
	/** id da entrada de histórico deste overlay enquanto ela está empilhada (null = não está). */
	guardId?: string | null;
	dismissing?: boolean;
}

let guardSeq = 0;

@Injectable({ providedIn: 'root' })
export class OverlayService {
	private stack: OverlayInstance[] = [];
	/** `history.back()` disparados pelo PRÓPRIO overlay ao fechar — o popstate deles não é o cliente. */
	private selfBacks = 0;
	private popstateListening = false;
	private readonly onPopState = () => this.handlePopState();

	constructor(
		private appRef: ApplicationRef,
		private envInjector: EnvironmentInjector,
	) {}

	/**
	 * Topo da pilha — do TIPO pedido, quando há um.
	 *
	 * O `kind` não é um detalhe: no Ionic cada controller tem a sua pilha, então
	 * `ModalController.getTop()` nunca devolve um `ion-loading`. Aqui a pilha é uma só, e sem
	 * o filtro um loading aberto por cima de um modal passava a ser "o modal do topo" — era o
	 * que fazia o `goDismiss()` do login fechar o loading, achar que já tinha fechado tudo e
	 * nunca navegar para o cardápio (QA OP-1075, C67). Ver overlay-pilha-por-tipo.spec.ts.
	 *
	 * Sem `kind` continua devolvendo o topo geral — é o que o guarda do Esc quer saber
	 * ("sou eu que estou na frente?"), e aí o tipo não importa.
	 */
	getTop(kind?: OverlayKind): OverlayHandle | undefined {
		const doTipo = kind ? this.stack.filter(i => i.kind === kind) : this.stack;
		return doTipo.length ? doTipo[doTipo.length - 1].handle : undefined;
	}

	/** Fecha o overlay do topo — do TIPO pedido, quando há um (ver getTop). */
	dismissTop(data?: any, role?: OverlayRole, kind?: OverlayKind): Promise<boolean> {
		const top = this.getTop(kind);
		if (!top) return Promise.resolve(false);
		return top.dismiss(data, role);
	}

	create(cfg: OverlayAttachConfig): OverlayHandle {
		const animated = cfg.animated !== false;
		const classes = Array.isArray(cfg.cssClass)
			? cfg.cssClass
			: cfg.cssClass
				? cfg.cssClass.split(/\s+/).filter(Boolean)
				: [];

		// --- host / backdrop / wrapper ---
		const hostEl = document.createElement('div');
		hostEl.className = ['mp-overlay', `mp-overlay--${cfg.kind}`, ...classes].join(' ');
		hostEl.setAttribute('role', 'dialog');
		hostEl.setAttribute('aria-modal', 'true');

		const backdropEl = document.createElement('div');
		backdropEl.className = 'mp-overlay-backdrop';
		if (cfg.showBackdrop === false) backdropEl.style.opacity = '0';

		const wrapperEl = document.createElement('div');
		wrapperEl.className = 'mp-overlay-wrapper';

		const contentEl = document.createElement('div');
		contentEl.className = 'mp-overlay-content';
		wrapperEl.appendChild(contentEl);

		hostEl.appendChild(backdropEl);
		hostEl.appendChild(wrapperEl);

		// --- conteúdo ---
		let componentRef: ComponentRef<any> | undefined;
		if (cfg.component) {
			componentRef = createComponent(cfg.component, { environmentInjector: cfg.environmentInjector ?? this.envInjector });
			if (cfg.componentProps) Object.assign(componentRef.instance, cfg.componentProps);
			this.appRef.attachView(componentRef.hostView);
			const el = componentRef.location.nativeElement as HTMLElement;
			el.classList.add('mp-overlay-page');
			contentEl.appendChild(el);
			componentRef.changeDetectorRef.detectChanges();
		} else if (cfg.element) {
			cfg.element.classList.add('mp-overlay-page');
			contentEl.appendChild(cfg.element);
		}

		// --- popover: posiciona junto ao alvo ---
		if (cfg.kind === 'popover' && cfg.positionTarget) {
			wrapperEl.style.position = 'fixed';
			wrapperEl.style.left = `${cfg.positionTarget.x}px`;
			wrapperEl.style.top = `${cfg.positionTarget.y}px`;
		}

		// --- promessas de dismiss ---
		let resolveDid: (v: OverlayDismissEvent) => void;
		let resolveWill: (v: OverlayDismissEvent) => void;
		const didDismiss = new Promise<OverlayDismissEvent>(r => (resolveDid = r));
		const willDismiss = new Promise<OverlayDismissEvent>(r => (resolveWill = r));

		let presented = false;
		let dismissed = false;

		const handle: OverlayHandle = {
			hostElement: hostEl,
			present: () => {
				if (presented) return Promise.resolve();
				presented = true;
				document.body.appendChild(hostEl);
				this.lockScroll();
				const inst = this.stack.find(i => i.hostEl === hostEl);
				if (inst) {
					inst.previousActive = document.activeElement as HTMLElement;
					// Toast não rouba foco (como no Ionic): tirar o foco de um campo fecharia o
					// teclado de quem está digitando quando o aviso aparece.
					if (cfg.kind !== 'toast') this.trapFocus(wrapperEl);
				}
				// força reflow antes de aplicar a classe de entrada (transição CSS)
				void hostEl.offsetWidth;
				hostEl.classList.add('mp-overlay--visible');
				if (cfg.duration && cfg.duration > 0 && inst) {
					inst.autoDismissTimer = setTimeout(() => handle.dismiss(undefined, 'timeout'), cfg.duration);
				}
				if (inst?.interceptHistoryBack) this.pushHistoryGuard(inst);
				return this.wait(animated ? ENTER_MS : 0);
			},
			dismiss: (data?: any, role?: OverlayRole) => {
				if (dismissed) return Promise.resolve(false);
				dismissed = true;
				resolveWill({ data, role });
				const inst = this.stack.find(i => i.hostEl === hostEl);
				if (inst) inst.dismissing = true;
				if (inst?.autoDismissTimer) clearTimeout(inst.autoDismissTimer);
				hostEl.classList.remove('mp-overlay--visible');
				hostEl.classList.add('mp-overlay--leaving');
				// A entrada de histórico sai ANTES do `onDidDismiss`: quem fecha costuma navegar logo
				// em seguida (salvar → pagamento), e um `history.back()` atrasado desfaria ESSA navegação.
				return Promise.all([this.wait(animated ? LEAVE_MS : 0), this.consumeHistoryGuard(inst)]).then(() => {
					this.teardown(hostEl, componentRef, inst);
					resolveDid({ data, role });
					return true;
				});
			},
			onDidDismiss: <D = any>() => didDismiss as Promise<OverlayDismissEvent<D>>,
			onWillDismiss: <D = any>() => willDismiss as Promise<OverlayDismissEvent<D>>,
			querySelector: <E extends Element = Element>(selectors: string) => hostEl.querySelector<E>(selectors),
		};

		const instance: OverlayInstance = { handle, kind: cfg.kind, hostEl, componentRef, interceptHistoryBack: !!cfg.interceptHistoryBack };
		this.stack.push(instance);

		// backdrop / esc
		if (cfg.backdropDismiss !== false) {
			backdropEl.addEventListener('click', () => handle.dismiss(undefined, 'backdrop'));
			instance.keyHandler = (e: KeyboardEvent) => {
				if (e.key === 'Escape' && this.getTop() === handle) handle.dismiss(undefined, 'backdrop');
			};
			document.addEventListener('keydown', instance.keyHandler);
		}

		return handle;
	}

	/**
	 * Empilha uma entrada de histórico com a MESMA URL, marcada com o id do overlay. O "voltar" do
	 * cliente então só tira essa entrada: a URL não muda, e o roteador ignora navegação para a URL
	 * em que já está (`onSameUrlNavigation: 'ignore'`, o padrão) — a página de baixo fica onde está.
	 * O `history.state` do roteador é preservado na entrada nova (ele guarda o `navigationId` ali).
	 */
	private pushHistoryGuard(inst: OverlayInstance) {
		try {
			if (typeof history === 'undefined' || typeof window === 'undefined') return;
			if (!this.popstateListening) {
				window.addEventListener('popstate', this.onPopState);
				this.popstateListening = true;
			}
			const id = `g${++guardSeq}`;
			history.pushState({ ...(history.state || {}), [GUARD_KEY]: id }, '');
			inst.guardId = id;
		} catch (e) {
			// histórico selado (iframe sandbox): sem guarda, comportamento antigo
			inst.guardId = null;
		}
	}

	/**
	 * Fechamento normal (seta, salvar, backdrop): tira a entrada que o overlay empilhou, se ela
	 * ainda for a atual, e espera o popstate dela (com teto). Se o app já navegou por cima dela,
	 * não mexe — um `history.back()` ali desfaria a navegação do app.
	 */
	private consumeHistoryGuard(inst?: OverlayInstance): Promise<void> {
		if (!inst || !inst.guardId) return Promise.resolve();
		const id = inst.guardId;
		inst.guardId = null;
		try {
			if (!history.state || history.state[GUARD_KEY] !== id) return Promise.resolve();
			return new Promise<void>(resolve => {
				let done = false;
				const fim = () => {
					if (done) return;
					done = true;
					window.removeEventListener('popstate', fim);
					// NÃO resolver já: o roteador do Angular trata o popstate num `setTimeout(0)`
					// (`navigateToSyncWithBrowser`). Resolvendo aqui, quem fecha navega (salvar →
					// pagamento) e DEPOIS o roteador executa a navegação atrasada de volta à URL da
					// guarda — desfazendo a navegação do app. O setTimeout abaixo entra na fila DEPOIS
					// do dele. Pego pelo spec `overlay-voltar-com-roteador` (Router e Location reais).
					setTimeout(resolve, 20);
				};
				window.addEventListener('popstate', fim);
				setTimeout(fim, HISTORY_CONSUME_MS);
				this.selfBacks++;
				history.back();
			});
		} catch (e) {
			return Promise.resolve();
		}
	}

	/** O cliente apertou "voltar" com um overlay-guarda aberto. */
	private handlePopState() {
		if (this.selfBacks > 0) {
			this.selfBacks--;
			return;
		}
		// O guarda mais de cima cuja entrada acabou de sair (não é mais o estado atual).
		const atual = history.state ? history.state[GUARD_KEY] : undefined;
		const guardas = this.stack.filter(i => i.guardId && !i.dismissing);
		const alvo = guardas.length ? guardas[guardas.length - 1] : undefined;
		if (!alvo || alvo.guardId === atual) return;
		alvo.guardId = null;

		const topo = this.stack[this.stack.length - 1];
		if (topo && topo !== alvo) {
			// Um alerta/loading por cima do overlay-guarda: o voltar fecha o de cima, como no Android.
			topo.handle.dismiss(undefined, 'back');
		} else {
			const comp = alvo.componentRef?.instance;
			try {
				if (comp && typeof comp.onHistoryBack === 'function') comp.onHistoryBack();
				else alvo.handle.dismiss(undefined, 'back');
			} catch (e) {
				alvo.handle.dismiss(undefined, 'back');
			}
		}
		// Continua aberto (voltou uma etapa, ou fechou o de cima)? Rearma para o próximo voltar.
		setTimeout(() => {
			if (this.stack.includes(alvo) && !alvo.dismissing && !alvo.guardId) this.pushHistoryGuard(alvo);
		}, 0);
	}

	private teardown(hostEl: HTMLElement, componentRef?: ComponentRef<any>, inst?: OverlayInstance) {
		if (inst?.keyHandler) document.removeEventListener('keydown', inst.keyHandler);
		if (componentRef) {
			this.appRef.detachView(componentRef.hostView);
			componentRef.destroy();
		}
		hostEl.remove();
		this.stack = this.stack.filter(i => i.hostEl !== hostEl);
		if (inst?.previousActive && typeof inst.previousActive.focus === 'function') {
			inst.previousActive.focus();
		}
		if (!this.stack.length) this.unlockScroll();
	}

	/**
	 * Foco inicial do overlay, como o Ionic faz em `present()`: vai para o PRÓPRIO overlay
	 * (`tabindex="-1"`), não para o primeiro botão de dentro. Focar o primeiro botão desenhava
	 * o anel de foco em volta da seta de voltar de todo modal aberto (visível nos prints do QA
	 * OP-1075 do login e da tela de endereço); o Ionic só leva o foco para dentro quando a
	 * pessoa usa o Tab — e o Tab a partir do wrapper chega no primeiro focável pela ordem do DOM.
	 * `[autofocus]` explícito no conteúdo continua valendo.
	 */
	private trapFocus(wrapperEl: HTMLElement) {
		const explicito = wrapperEl.querySelector<HTMLElement>('[autofocus]');
		if (explicito) {
			explicito.focus?.();
			return;
		}
		if (!wrapperEl.hasAttribute('tabindex')) wrapperEl.setAttribute('tabindex', '-1');
		wrapperEl.focus?.({ preventScroll: true });
	}

	private lockScroll() {
		if (this.stack.length <= 1) document.body.classList.add('mp-overlay-open');
	}

	private unlockScroll() {
		document.body.classList.remove('mp-overlay-open');
	}

	private wait(ms: number): Promise<void> {
		return new Promise(resolve => setTimeout(resolve, ms));
	}
}
